import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { FilePlus2, Plus } from 'lucide-react';
import { Badge, Button, Card, Input, Modal, Select, Spinner } from '@/components/ui';
import { usePermissions } from '@/hooks/usePermissions';
import {
  createTemplate,
  DOCUMENT_TYPE_LABELS,
  generateDocument,
  listTemplates,
  type HrDocumentStatus,
  type HrDocumentType,
} from '@/api/services/hrDocuments.service';
import { listHrDocuments, type HrPersonRef } from '@/api/services/hrWorkspace.service';
import { EmployeePicker } from '../people/EmployeePicker';
import { SetupSectionIntro, type HrAreaProps } from '../hrShared';
import { useOpenFromQuery } from '@/hooks/useOpenFromQuery';
import { getErrorMessage } from '@/lib/getErrorMessage';
import { LETTER_STATUS, formatLetterDate } from '../letters/letterFormat';
import { VERSION_STATUS } from './documentFormat';
import { TemplateEditorDialog } from './TemplateEditorDialog';
import { DocumentDetailDialog } from './DocumentDetailDialog';

const TYPE_OPTIONS = (Object.keys(DOCUMENT_TYPE_LABELS) as HrDocumentType[]).map((value) => ({
  value,
  label: DOCUMENT_TYPE_LABELS[value],
}));
/** Letters that can carry pay details; offered only with `hr:directory`. */
const PAY_TYPES: HrDocumentType[] = ['offer', 'appointment', 'salary_revision'];

function CreateTemplateDialog({
  companyId,
  canUsePay,
  onCreated,
  onClose,
}: {
  companyId: string;
  canUsePay: boolean;
  onCreated: (templateId: string) => void;
  onClose: () => void;
}): React.ReactElement {
  const queryClient = useQueryClient();
  const [type, setType] = useState<HrDocumentType>('confirmation');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: () => createTemplate(companyId, { type, name: name.trim() }),
    onSuccess: ({ template }) => {
      void queryClient.invalidateQueries({ queryKey: ['hr-document-templates', companyId] });
      onCreated(template._id);
    },
    onError: (mutationError) => setError(getErrorMessage(mutationError)),
  });
  return (
    <Modal open onClose={onClose} title="New template" className="max-w-md">
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          if (name.trim().length >= 2) create.mutate();
        }}
      >
        <Select
          label="Letter type"
          value={type}
          onChange={(value) => setType(value as HrDocumentType)}
          options={TYPE_OPTIONS.filter((option) => canUsePay || !PAY_TYPES.includes(option.value))}
        />
        <Input
          id="template-name"
          label="Template name"
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
        <p className="text-xs text-gray-600 dark:text-gray-300">
          It starts from standard wording you can edit. Another HR user approves it before it can be
          used.
        </p>
        {error && (
          <p className="text-sm text-danger" role="alert">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            size="sm"
            disabled={name.trim().length < 2}
            loading={create.isPending}
          >
            Create
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function GenerateDialog({
  companyIds,
  showCompany,
  onGenerated,
  onClose,
}: HrAreaProps & {
  onGenerated: (document: { id: string; companyId: string }) => void;
  onClose: () => void;
}): React.ReactElement {
  const queryClient = useQueryClient();
  const [person, setPerson] = useState<HrPersonRef | null>(null);
  const [templateId, setTemplateId] = useState('');
  const [requestId] = useState(() => crypto.randomUUID());
  const [error, setError] = useState<string | null>(null);
  const companyId = person?.companyId ?? '';
  const { data: templates = [] } = useQuery({
    queryKey: ['hr-document-templates', companyId],
    queryFn: () => listTemplates(companyId),
    enabled: companyId !== '',
  });
  const generate = useMutation({
    mutationFn: () =>
      generateDocument(companyId, { templateId, userId: person?.userId ?? '', requestId }),
    onSuccess: (document) => {
      void queryClient.invalidateQueries({ queryKey: ['hr-documents'] });
      onGenerated({ id: document._id, companyId });
    },
    onError: (mutationError) => setError(getErrorMessage(mutationError)),
  });
  const approved = templates.filter((template) => template.currentVersion);
  const templatePlaceholder = !person
    ? 'Choose an employee first'
    : approved.length
      ? 'Choose a letter'
      : 'This company has no approved letter templates yet';
  return (
    <Modal open onClose={onClose} title="Generate a letter" className="max-w-md">
      <div className="space-y-4">
        <EmployeePicker
          id="generate-letter-employee"
          companyIds={companyIds}
          showCompany={showCompany}
          value={person}
          onChange={(next) => {
            setPerson(next);
            // Templates belong to the person's company.
            setTemplateId('');
          }}
        />
        <Select
          label="Letter"
          value={templateId}
          onChange={setTemplateId}
          disabled={!person}
          placeholder={templatePlaceholder}
          options={approved.map((template) => ({
            value: template._id,
            label: `${template.name} · ${DOCUMENT_TYPE_LABELS[template.type]}`,
          }))}
        />
        <p className="text-xs text-gray-600 dark:text-gray-300">
          The letter is filled from the employee&apos;s current HR records. You can review it before
          sending it for signature.
        </p>
        {error && (
          <p className="text-sm text-danger" role="alert">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={!templateId || !person}
            loading={generate.isPending}
            onClick={() => generate.mutate()}
          >
            Generate
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/** Letter templates for one company, shown in Setup. */
export function TemplatesPanel({ companyId }: { companyId: string }): React.ReactElement {
  const { can } = usePermissions();
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const { data, isLoading, error } = useQuery({
    queryKey: ['hr-document-templates', companyId],
    queryFn: () => listTemplates(companyId),
  });
  const templates = Array.isArray(data) ? data : [];

  return (
    <div className="space-y-4">
      <SetupSectionIntro
        action={
          <Button size="sm" onClick={() => setCreating(true)}>
            <Plus className="mr-1 h-4 w-4" aria-hidden />
            New template
          </Button>
        }
      >
        The standard wording for offer, confirmation and other letters. A second HR user approves
        each template before it can be used. Letters are then generated for a person from Letters
        &amp; policies, filled in from their HR record.
      </SetupSectionIntro>
      <Card contentClassName="p-0">
        {isLoading ? (
          <div className="flex justify-center py-10" role="status">
            <Spinner />
            <span className="sr-only">Loading templates</span>
          </div>
        ) : error ? (
          <p className="p-4 text-sm text-danger" role="alert">
            {getErrorMessage(error)}
          </p>
        ) : templates.length === 0 ? (
          <div className="flex flex-col items-start gap-3 px-5 py-8">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              No templates yet. Most companies start with an offer letter and a confirmation letter.
            </p>
            <Button size="sm" variant="secondary" onClick={() => setCreating(true)}>
              Create the first template
            </Button>
          </div>
        ) : (
          <ul className="divide-y divide-gray-200 dark:divide-gray-700">
            {templates.map((template) => {
              const open = template.openVersion;
              return (
                <li
                  key={template._id}
                  className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                      {template.name}
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-300">
                      {DOCUMENT_TYPE_LABELS[template.type]}
                      {template.currentVersion
                        ? ` · version ${template.currentVersion.version} in use`
                        : ' · can’t be used until approved'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {open && (
                      <Badge variant={VERSION_STATUS[open.status].variant}>
                        {VERSION_STATUS[open.status].label}
                      </Badge>
                    )}
                    <Button size="sm" variant="secondary" onClick={() => setOpenId(template._id)}>
                      Open <span className="sr-only">{template.name}</span>
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        {creating && (
          <CreateTemplateDialog
            companyId={companyId}
            canUsePay={can('hr', 'directory')}
            onClose={() => setCreating(false)}
            onCreated={(id) => {
              setCreating(false);
              setOpenId(id);
            }}
          />
        )}
        {openId && (
          <TemplateEditorDialog
            key={openId}
            companyId={companyId}
            templateId={openId}
            onClose={() => setOpenId(null)}
          />
        )}
      </Card>
    </div>
  );
}

const STATUS_FILTERS: Array<{ value: HrDocumentStatus | ''; label: string }> = [
  { value: '', label: 'All statuses' },
  { value: 'generated', label: 'Not sent' },
  { value: 'issued', label: 'Awaiting signature' },
  { value: 'partially_signed', label: 'Partly signed' },
  { value: 'completed', label: 'Signed' },
  { value: 'refused', label: 'Declined' },
  { value: 'expired', label: 'Expired' },
  { value: 'voided', label: 'Withdrawn' },
];

/** Letters across companies and their signing progress. Gated by `hr:documents`. */
export function HrDocumentsTab({ companyIds, showCompany }: HrAreaProps): React.ReactElement {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<HrDocumentStatus | ''>(() => {
    const requested = searchParams.get('status');
    return STATUS_FILTERS.some((option) => option.value === requested)
      ? (requested as HrDocumentStatus)
      : '';
  });
  const [page, setPage] = useState(1);
  const [generating, setGenerating] = useState(false);
  useOpenFromQuery(setGenerating);
  const [selected, setSelected] = useState<{ id: string; companyId: string } | null>(null);
  const { data, isLoading, error } = useQuery({
    queryKey: ['hr-documents', companyIds?.join(',') ?? '', status, page],
    queryFn: () => listHrDocuments(companyIds, { ...(status && { status }), page, limit: 25 }),
  });
  const rows = Array.isArray(data?.data) ? data.data : [];
  const total = data?.total ?? 0;

  return (
    <Card
      header={
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="w-full sm:w-48">
            <Select
              label="Status"
              value={status}
              onChange={(value) => {
                setStatus(value as HrDocumentStatus | '');
                setPage(1);
              }}
              options={STATUS_FILTERS}
            />
          </div>
          <Button size="sm" onClick={() => setGenerating(true)}>
            <FilePlus2 className="mr-1 h-4 w-4" aria-hidden />
            Generate a letter
          </Button>
        </div>
      }
      contentClassName="p-0"
    >
      {isLoading ? (
        <div className="flex justify-center py-10" role="status">
          <Spinner />
          <span className="sr-only">Loading letters</span>
        </div>
      ) : error ? (
        <p className="p-4 text-sm text-danger" role="alert">
          {getErrorMessage(error)}
        </p>
      ) : rows.length === 0 ? (
        <p className="p-6 text-center text-sm text-gray-600 dark:text-gray-300">
          {status
            ? 'No letters with this status.'
            : 'No letters yet. Generate one from a template set up for the company.'}
        </p>
      ) : (
        <ul className="divide-y divide-gray-200 dark:divide-gray-700">
          {rows.map((row) => {
            const badge = LETTER_STATUS[row.status];
            return (
              <li
                key={row._id}
                className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                    {row.title}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-300">
                    {[
                      row.employee?.name ?? 'Employee',
                      showCompany ? row.companyName : null,
                      formatLetterDate(row.createdAt),
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={badge.variant}>{badge.label}</Badge>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => setSelected({ id: row._id, companyId: row.companyId })}
                  >
                    Open <span className="sr-only">{row.title}</span>
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {total > 25 && (
        <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3 text-sm dark:border-gray-700">
          <span className="text-gray-600 dark:text-gray-300">
            Page {page} of {Math.ceil(total / 25)}
          </span>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={page * 25 >= total}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
      {generating && (
        <GenerateDialog
          companyIds={companyIds}
          showCompany={showCompany}
          onClose={() => setGenerating(false)}
          onGenerated={(document) => {
            setGenerating(false);
            toast.success('Letter generated. Review it, then send it for signature.');
            setSelected(document);
          }}
        />
      )}
      {selected && (
        <DocumentDetailDialog
          key={selected.id}
          companyId={selected.companyId}
          documentId={selected.id}
          onClose={() => setSelected(null)}
        />
      )}
    </Card>
  );
}
